import {useDataContext} from "../../SurveyContextProvider";
import {useAuthData, useCustomerSegmentData, queryClient} from "../../../App";
import {useSnackbar} from "../../../Content";
import {useIntl} from "react-intl";
import {PostSurveyWithId, PostUsersToAnnieUserSurvey, UpdateSurveyWithIdAndUpdateContext, GetGeneralTranslations} from "../../../api/APISurvey";
import {useRef, useState} from "react";
import {GetAssistant, PostAssistant, PostAssistantWithId, CreateVectorStore, AddFileToVectorStore, RemoveFileFromVectorStore, GetVectorStoreFiles} from "../../../api/APIBot";
import {PostAIAgent, PostAIAgentWithId, GetAIAgent} from "../../../api/APIGemini";
import {useAssistantOperations, gatherFilesFromBots} from "../../../utils/assistantUtils";
import {getAIProvider} from "../../../utils/aiProviderUtils";
import {ensureRagRetrievalTool} from "../../../utils/ragCorpusUtils";
import {buildGeminiAgentData} from "../../../utils/geminiConfigUtils";
import {clearValueFromSessionStorage, reconstructConfigObject, removeUnnecessaryKeys} from "../../../DataFunctions";
import {getDefaultToneForSegment} from "../../SupportBotEditor/components/ConversationToneToggle";
import {getTopicDisplayNameFromTranslations} from "../../../utils/topicUtils";
import _ from "lodash";
import {updateTimeToServerFormat} from "../../../Formats";
import {formatTranslationsToIntl} from "../../../Formats";

export function useSurveySave() {
    const { value: surveyData, setValue: setSurveyData } = useDataContext('surveyData');
    const { value: userSurveyData } = useDataContext('userSurveyData');
    const { value: originalSurveyData, setValue: setOriginalSurveyData } = useDataContext('originalSurveyData');
    const { value: originalUserSurveyData, setValue: setOriginalUserSurveyData } = useDataContext('originalUserSurveyData');
    const { value: supportBots } = useDataContext('supportBots');
    const { value: categoryData } = useDataContext('categoryData');
    const { value: supportRolesData } = useDataContext('supportRolesData');

    const { authData } = useAuthData();
    const { customerSegment } = useCustomerSegmentData();
    const { setSnackbarData } = useSnackbar();
    const intl = useIntl();

    // Local loading state to track save operation from start to finish (null, 'DRAFT', or 'ACTIVE')
    const [isSaving, setIsSaving] = useState(null);

    // Detect AI provider
    const { value: instanceConfig } = useDataContext('instanceConfig');
    const aiProvider = getAIProvider(instanceConfig);
    const isGemini = aiProvider === "gemini";

    const { value: geminiConfig } = useDataContext('geminiConfig');

    const normalizeOptions = {
        aiProvider,
        defaultTone: getDefaultToneForSegment(customerSegment),
        supportRoles: supportRolesData
    };
    const surveyUpdate = UpdateSurveyWithIdAndUpdateContext(surveyData.id, setSurveyData, setOriginalSurveyData, normalizeOptions);
    const newID = useRef(new Date().getTime().toString());
    const surveyPost = PostSurveyWithId(newID.current);
    const annieUserSurveyPost = PostUsersToAnnieUserSurvey();

    // OpenAI hooks
    const assistantId = surveyData?.config?.assistantid || "";
    const postAssistantWithId = PostAssistantWithId(assistantId);
    const postAssistant = PostAssistant();
    const getAssistant = GetAssistant(assistantId, !isGemini && surveyData?.config?.type === "SUPPORTBOT");

    // Gemini hooks
    const aiAgentId = surveyData?.config?.aiagent ? Number(surveyData.config.aiagent) : "";
    const postAIAgent = PostAIAgent();
    const postAIAgentWithId = PostAIAgentWithId(aiAgentId);
    const getAIAgent = GetAIAgent(aiAgentId, isGemini && surveyData?.config?.type === "SUPPORTBOT");

    // OpenAI vector store hooks
    const createVectorStore = CreateVectorStore();
    const addFileToVectorStore = AddFileToVectorStore();
    const removeFileFromVectorStore = RemoveFileFromVectorStore();
    const { refetch: refetchVectorStoreFiles } = GetVectorStoreFiles(
        surveyData?.config?.vector_store_id,
        false
    );

    // Fetch translations in survey language for initial greeting
    const surveyLang = surveyData?.config?.language;
    const surveyTranslations = GetGeneralTranslations(surveyLang);

    // Use the shared hook for assistant operations
    const { buildAssistantInstructions, fetchCombinedToolsAndResources } = useAssistantOperations();

    const clearSessionStorage = (newId) => {
        clearValueFromSessionStorage('surveyData', surveyData.id);
        clearValueFromSessionStorage('userSurveyData', surveyData.id);
        clearValueFromSessionStorage('surveyData', 'new');
        clearValueFromSessionStorage('userSurveyData', 'new');
        if (newId) {
            clearValueFromSessionStorage('surveyData', newId);
            clearValueFromSessionStorage('userSurveyData', newId);
        }
    };

    const handleSaveError = (error) => {
        setSnackbarData({
            show: true,
            text: intl.formatMessage({
                id: 'toast.saveFailed',
                defaultMessage: 'The save failed. Please try again.',
            }),
            state: "error",
            autoHide: true,
        });
    };

    const handleSaveSuccess = (callback) => {
        setSnackbarData({
            show: true,
            text: intl.formatMessage({
                id: 'toast.saveSuccess',
                defaultMessage: 'Survey Saved!',
            }),
            state: "success",
            autoHide: true,
        });
        if (callback) callback();
    };

    const processSurveyUpdate = (isNewSurvey, isDraft, callback, cleanedSurveyData) => {
        if (isNewSurvey) {
            cleanedSurveyData.id = newID.current;
            const surveyDataForPost = reconstructConfigObject(cleanedSurveyData);

            surveyPost.mutate(surveyDataForPost, {
                onError: handleSaveError,
                onSuccess: (response) => {
                    if (userSurveyData.length < 1) {
                        clearSessionStorage();
                        if (callback) {
                            callback(newID.current);
                        }
                    } else {
                        const newAnnieUserSurveyData = [...userSurveyData];
                        newAnnieUserSurveyData.forEach(annieuser => annieuser.survey = newID.current);
                        annieUserSurveyPost.mutate(newAnnieUserSurveyData, {
                            onError: handleSaveError,
                            onSuccess: () => {
                                clearSessionStorage();
                                if (callback) callback(newID.current);
                            }
                        });
                    }
                }
            });
        } else {
            const surveyDataForPost = reconstructConfigObject(cleanedSurveyData);

            surveyUpdate.mutate(surveyDataForPost, {
                onError: handleSaveError,
                onSuccess: () => {
                    if (userSurveyData.length < 1) {
                        setOriginalUserSurveyData(_.cloneDeep(userSurveyData));
                        clearSessionStorage();
                        handleSaveSuccess(callback);
                    } else {
                        annieUserSurveyPost.mutate(userSurveyData, {
                            onError: handleSaveError,
                            onSuccess: () => {
                                setOriginalUserSurveyData(_.cloneDeep(userSurveyData));
                                clearSessionStorage();
                                handleSaveSuccess(callback);
                            }
                        });
                    }
                }
            });
        }
    };

    const handleSurveyVectorStore = async (surveyDataToSave, supportBots) => {
        const selectedBotIds = surveyDataToSave?.config?.supportBots || [];

        // Gather all file IDs from selected bot modules
        const fileIds = gatherFilesFromBots(selectedBotIds, supportBots);

        // If no files, return null (don't create vector store)
        if (fileIds.length === 0) {
            return null;
        }

        let currentVectorStoreId = surveyDataToSave.config?.vector_store_id;

        // Create vector store if it doesn't exist
        if (!currentVectorStoreId) {
            try {
                const createRes = await createVectorStore.mutateAsync({});
                currentVectorStoreId = createRes?.id;
                if (!currentVectorStoreId) {
                    throw new Error("Vector store creation failed - no ID in response");
                }
            } catch (error) {
                console.error("Error creating survey vector store:", error);
                throw error;
            }
        }

        // Sync files to the vector store
        try {
            let existingFileIds = new Set();

            // Fetch existing files if vector store already existed
            if (surveyDataToSave.config?.vector_store_id) {
                const { data: currentFiles } = await refetchVectorStoreFiles();
                existingFileIds = new Set(currentFiles?.data?.map(f => f.id) || []);
            }

            const newFileIds = new Set(fileIds);

            // Add new files
            for (const fileId of fileIds) {
                if (!existingFileIds.has(fileId)) {
                    console.log(`Adding file ${fileId} to survey vector store ${currentVectorStoreId}`);
                    await addFileToVectorStore.mutateAsync({
                        vectorStoreId: currentVectorStoreId,
                        fileId
                    });
                }
            }

            // Remove files that are no longer in any selected bot
            for (const existingFileId of existingFileIds) {
                if (!newFileIds.has(existingFileId)) {
                    console.log(`Removing file ${existingFileId} from survey vector store ${currentVectorStoreId}`);
                    await removeFileFromVectorStore.mutateAsync({
                        vectorStoreId: currentVectorStoreId,
                        fileId: existingFileId
                    });
                }
            }
        } catch (error) {
            console.error("Error syncing files with survey vector store:", error);
            throw error;
        }

        return currentVectorStoreId;
    };

    const saveSurvey = async (isNewSurvey, isDraft, callback, saveType = null) => {
        setIsSaving(saveType || (isDraft ? 'DRAFT' : 'ACTIVE'));

        const newSurveyData = {...surveyData};
        const cleanedSurveyData = removeUnnecessaryKeys(newSurveyData);

        cleanedSurveyData.updated = updateTimeToServerFormat(new Date());
        cleanedSurveyData.updatedby = authData.uid;

        if (isDraft) {
            cleanedSurveyData.status = "DRAFT";
        } else if (cleanedSurveyData.status === "DRAFT") {
            cleanedSurveyData.status = "SCHEDULED";
        }

        setSurveyData(cleanedSurveyData);

        if (surveyData?.config?.type === "SUPPORTBOT") {
            try {
                // 0. Force-refresh the bot list so the agent is built from the current bot
                // modules even if the editor has been open a while or a bot was edited
                // elsewhere (another tab / another user). Fall back to context on failure.
                let freshSupportBots = supportBots;
                try {
                    await queryClient.refetchQueries("bots", { exact: true });
                    freshSupportBots = queryClient.getQueryData("bots") || supportBots;
                } catch (e) {
                    freshSupportBots = supportBots;
                }

                // 1. For OpenAI: handle vector store creation and file syncing
                let surveyVectorStoreId = null;
                if (!isGemini) {
                    try {
                        surveyVectorStoreId = await handleSurveyVectorStore(cleanedSurveyData, freshSupportBots);
                        if (surveyVectorStoreId) {
                            cleanedSurveyData.config.vector_store_id = surveyVectorStoreId;
                        }
                    } catch (error) {
                        setIsSaving(null);
                        setSnackbarData({
                            show: true,
                            text: intl.formatMessage({
                                id: 'vectorStoreSaveFailed',
                                defaultMessage: 'Failed to save reference materials. Please try again.'
                            }),
                            state: "error",
                            autoHide: true,
                        });
                        return;
                    }
                }

                // 2. Build assistant instructions using shared function
                const surveyId = surveyData?.id === "new" ? newID.current : surveyData?.id;
                const geminiMasterPrompt = isGemini ? geminiConfig?.systemInstructionTemplate : undefined;
                const conversationTone = isGemini ? (surveyData?.config?.conversationTone || 'professional') : null;
                const processedAssistantData = buildAssistantInstructions(surveyData, surveyId, false, geminiMasterPrompt, isGemini ? geminiConfig : undefined, surveyTranslations?.data, conversationTone);

                if (isGemini) {
                    // --- Gemini: merge corpora from all selected bot modules ---
                    const selectedBotIds = cleanedSurveyData?.config?.supportBots || [];
                    const mergedCorpora = {};
                    selectedBotIds.forEach(botId => {
                        const bot = freshSupportBots?.find(b => Number(b.id) === Number(botId));
                        Object.entries(bot?.config?.corpora || {}).forEach(([slug, corpusId]) => {
                            // Dedup by corpusId — if two bots share the same KB, keep one entry
                            if (!Object.values(mergedCorpora).includes(corpusId)) {
                                mergedCorpora[slug] = corpusId;
                            }
                        });
                    });

                    // Start with empty tools — template tools come from geminiConfig.tools via buildGeminiAgentData
                    // Only ensureRagRetrievalTool adds the search_knowledge_base function
                    const tools = ensureRagRetrievalTool([], mergedCorpora);

                    // Build initial contents (Gemini requires user turn first)
                    const userGreeting = surveyTranslations?.data?.['bot.initialGreeting'] || 'Hello';
                    const contents = [{ role: "user", parts: [{ text: userGreeting }] }];

                    const existingRagConfig = getAIAgent?.data?.config?.ragConfig;

                    // Resolve taxonomy topic names in survey language (not admin UI language)
                    const surveyTranslationMap = surveyTranslations?.data
                        ? formatTranslationsToIntl(surveyTranslations.data)
                        : null;
                    const resolvedCategoryData = categoryData
                        ? Object.fromEntries(
                            Object.entries(categoryData).map(([code, data]) => [
                                code,
                                { ...data, title: getTopicDisplayNameFromTranslations(code, data, surveyTranslationMap) }
                            ])
                        )
                        : {};

                    const agentData = buildGeminiAgentData(
                        geminiConfig,
                        processedAssistantData.instructions,
                        tools,
                        {
                            corpora: mergedCorpora,
                            supportBots: freshSupportBots,
                            selectedBotIds,
                            supportTopicCodes: resolvedCategoryData,
                            existingRagConfig,
                            contents
                        }
                    );

                    if (aiAgentId) {
                        if (getAIAgent.isLoading) {
                            setIsSaving(null);
                            return;
                        } else if (getAIAgent.isSuccess || !getAIAgent.isError) {
                            postAIAgentWithId.mutate(agentData, {
                                onError: (error) => {
                                    setIsSaving(null);
                                    handleSaveError(error);
                                },
                                onSuccess: () => {
                                    setIsSaving(null);
                                    processSurveyUpdate(isNewSurvey, isDraft, callback, cleanedSurveyData);
                                }
                            });
                        } else {
                            setIsSaving(null);
                            handleSaveError();
                        }
                    } else {
                        postAIAgent.mutate(agentData, {
                            onError: (error) => {
                                setIsSaving(null);
                                handleSaveError(error);
                            },
                            onSuccess: (response) => {
                                setIsSaving(null);
                                if (response?.id) {
                                    cleanedSurveyData.config.aiagent = Number(response.id);
                                    setSurveyData(cleanedSurveyData);
                                }
                                processSurveyUpdate(isNewSurvey, isDraft, callback, cleanedSurveyData);
                            }
                        });
                    }
                } else {
                    // --- OpenAI: existing flow ---
                    // 3. Fetch combined tools from bot modules (OpenAI only — per-bot assistants exist)
                    let combinedTools = [];
                    try {
                        const { tools } = await fetchCombinedToolsAndResources(surveyData?.config?.supportBots, aiProvider);
                        if (tools) {
                            combinedTools = tools;
                        }
                    } catch (error) {
                        console.error('Error fetching assistant tools:', error);
                    }

                    // 4. Ensure file_search tool is present if we have files
                    const selectedBotIds = cleanedSurveyData?.config?.supportBots || [];
                    const fileIds = gatherFilesFromBots(selectedBotIds, freshSupportBots);

                    let tools = combinedTools;
                    if (fileIds.length > 0) {
                        const hasFileSearchTool = tools.some(tool => tool.type === "file_search");
                        if (!hasFileSearchTool) {
                            tools = [...tools, { type: "file_search" }];
                        }
                    } else {
                        tools = tools.filter(tool => tool.type !== "file_search");
                    }

                    processedAssistantData.tools = tools;

                    // 5. Add vector store to tool_resources if available
                    if (surveyVectorStoreId) {
                        processedAssistantData.tool_resources = {
                            file_search: {
                                vector_store_ids: [surveyVectorStoreId]
                            }
                        };
                    }

                    // 6. Create or update assistant
                    if (assistantId) {
                        if (getAssistant.isLoading) {
                            setIsSaving(null);
                            return;
                        } else if (getAssistant.isSuccess && getAssistant.data) {
                            postAssistantWithId.mutate(processedAssistantData, {
                                onError: (error) => {
                                    setIsSaving(null);
                                    handleSaveError(error);
                                },
                                onSuccess: () => {
                                    setIsSaving(null);
                                    processSurveyUpdate(isNewSurvey, isDraft, callback, cleanedSurveyData);
                                }
                            });
                        } else if (getAssistant.isError) {
                            setIsSaving(null);
                            handleSaveError();
                        }
                    } else {
                        postAssistant.mutate(processedAssistantData, {
                            onError: (error) => {
                                setIsSaving(null);
                                handleSaveError(error);
                            },
                            onSuccess: (response) => {
                                setIsSaving(null);
                                if (response && response?.data?.id) {
                                    cleanedSurveyData.config.assistantid = response.data.id;
                                    setSurveyData(cleanedSurveyData);
                                }
                                processSurveyUpdate(isNewSurvey, isDraft, callback, cleanedSurveyData);
                            }
                        });
                    }
                }
            } catch (error) {
                console.error("Error saving SUPPORTBOT assistant/agent:", error);
                setIsSaving(null);
                setSnackbarData({
                    show: true,
                    text: intl.formatMessage({
                        id: 'assistantSaveFailed',
                        defaultMessage: 'Failed to save assistant. Please try again.'
                    }),
                    state: "error",
                    autoHide: true,
                });
                return;
            }
        } else {
            // Non-SUPPORTBOT surveys proceed directly
            setIsSaving(null);
            processSurveyUpdate(isNewSurvey, isDraft, callback, cleanedSurveyData);
        }
    };

    return {
        saveSurvey,
        clearSessionStorage,
        isSaving,
        isLoading: isSaving !== null || surveyUpdate.isLoading || surveyPost.isLoading ||
            postAssistantWithId.isLoading || postAssistant.isLoading ||
            postAIAgentWithId.isLoading || postAIAgent.isLoading
    };
}